/**
 * 사이즈 비교 함수 (공통 유틸리티)
 * - 숫자 사이즈: 오름차순 정렬 (230, 235, 240...)
 * - 문자 사이즈: 사이즈 우선순위 맵 기반 정렬 (S → M → L → XL → 2XL ...)
 * @param {string} a - 첫 번째 사이즈
 * @param {string} b - 두 번째 사이즈
 * @returns {number} - 정렬 순서
 */
function compareSizes(a, b) {
  var SIZE_ORDER = [
    'WXS', 'WS', 'WM', 'WL', 'WXL',
    'XXS', 'XS', 'S', 'M', 'L', 'XL', '2XL', 'XXL', '3XL', 'XXXL', '4XL',
    'F', 'FREE'
  ];

  var numA = parseInt(a, 10);
  var numB = parseInt(b, 10);

  // 둘 다 숫자로 시작하면 숫자로 정렬 (230, 235, 240...)
  if (!isNaN(numA) && !isNaN(numB)) return numA - numB;

  // 사이즈 문자열에서 letter 부분 추출
  // "S(090)" → "S", "2XL(110)" → "2XL", "XL" → "XL", "WM(W80)" → "WM"
  function extractSizeLetter(s) {
    var match = s.match(/^([A-Z0-9]*[A-Z]+)/i);
    return match ? match[1].toUpperCase() : s.toUpperCase();
  }

  var letterA = extractSizeLetter(a);
  var letterB = extractSizeLetter(b);
  var indexA = SIZE_ORDER.indexOf(letterA);
  var indexB = SIZE_ORDER.indexOf(letterB);

  // 둘 다 매핑에 있으면 우선순위로 정렬
  if (indexA !== -1 && indexB !== -1) return indexA - indexB;
  // 하나만 매핑에 있으면 매핑된 것 우선
  if (indexA !== -1) return -1;
  if (indexB !== -1) return 1;
  // 둘 다 없으면 문자열 비교
  return a.localeCompare(b);
}

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling && summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details')?.hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  if (!container) return;
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  if (elementToFocus) elementToFocus.focus();

  if (
    elementToFocus &&
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button):not(.menu-drawer__parent-button--exclude-close):not(.menu-drawer__search-close)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      // Keep mobile facets filter tabs open
      if (details.classList.contains('mobile-facets__details')) {
        return;
      }
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
    this.savedScrollY = 0;
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);

    // 스크롤 위치 저장 및 body 고정 (모바일에서 스크롤 위치 유지)
    this.savedScrollY = window.scrollY;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.top = `-${this.savedScrollY}px`;
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);

    // body 고정 해제 및 스크롤 위치 복원
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.width = '';
    document.body.style.top = '';
    window.scrollTo(0, this.savedScrollY);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    // On initial page load (not AJAX), clear all selected options and disable add to cart
    // Skip if this is a re-render from swatch/variant change (not initial load)
    // Check multiple conditions:
    // 1. data-skip-init attribute (set by product-info.js during swap)
    // 2. Window flag set during product swap
    // 3. Check if page was already loaded (DOMContentLoaded already fired)
    const isProductSwap = this.hasAttribute('data-skip-init') || window.__productSwapInProgress;
    const isInitialPageLoad = document.readyState === 'loading';

    // Only run initializeNoSelection on truly initial page load
    if (!isProductSwap && isInitialPageLoad) {
      this.initializeNoSelection();
    } else if (!isProductSwap && !isInitialPageLoad) {
      // Page already loaded, check if this is first variant-selects or a swap
      // If window.__variantSelectsInitialized is not set, this is the first load
      if (!window.__variantSelectsInitialized) {
        window.__variantSelectsInitialized = true;
        this.initializeNoSelection();
      }
    }

    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);

      // 품절된 옵션 클릭 시 variant 변경 방지
      if (target.classList && target.classList.contains('disabled')) {
        event.preventDefault();
        event.stopPropagation();
        // 선택 상태 되돌리기
        if (target.type === 'radio') {
          target.checked = false;
        }
        return;
      }

      // 사이즈 변경 시 카트 버튼 임시 disabled 처리 (비동기 상태 업데이트 완료 전까지)
      const productInfo = this.closest('product-info');
      if (productInfo) {
        const productForm = productInfo.querySelector('product-form');
        const submitButton = productForm?.querySelector('[data-atc-btn]');
        if (submitButton) {
          submitButton.setAttribute('data-variant-loading', '');
        }
      }

      // 사이즈 선택 시 드롭다운 즉시 닫기
      const sizeDropdown = this.querySelector('[data-varaint-dropdown]');
      if (sizeDropdown) {
        sizeDropdown.classList.remove('product-form__input--active');
        sizeDropdown.setAttribute('aria-expanded', 'false');
      }

      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });

    // Close size dropdown when clicking outside
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    document.addEventListener('click', this.handleOutsideClick);

    // 사이즈 옵션 정렬
    this.sortSizeOptions();

    // 모바일에서 사이즈 드롭다운 열릴 때 자동 스크롤
    const sizeDropdownEl = this.querySelector('[data-varaint-dropdown]');
    if (sizeDropdownEl) {
      this._handleSizeDropdownClick = () => this.scrollToSizeDropdown(sizeDropdownEl);
      sizeDropdownEl.addEventListener('click', this._handleSizeDropdownClick);
    }
  }

  /**
   * 사이즈 옵션을 정렬합니다 (PLP와 동일한 로직)
   * - 숫자 사이즈: 오름차순 정렬 (230, 235, 240...)
   * - 문자 사이즈: 사전식 정렬 (localeCompare)
   */
  sortSizeOptions() {
    const sizeDropdown = this.querySelector('[data-varaint-dropdown]');
    if (!sizeDropdown) return;

    const wrapper = sizeDropdown.querySelector('.product-form__input-wrapper');
    if (!wrapper) return;

    // input + label 쌍을 수집
    const inputs = wrapper.querySelectorAll('input[type="radio"]');
    if (inputs.length === 0) return;

    // 사이즈 데이터 수집 (input과 label 쌍)
    const sizeElements = [];
    inputs.forEach((input) => {
      const label = wrapper.querySelector(`label[for="${input.id}"]`);
      if (label) {
        sizeElements.push({
          size: input.value,
          input: input,
          label: label,
        });
      }
    });

    // 사이즈 정렬 (공통 유틸리티 함수 사용)
    sizeElements.sort((a, b) => compareSizes(a.size, b.size));

    // 정렬된 순서대로 DOM 재배치
    sizeElements.forEach(({ input, label }) => {
      wrapper.appendChild(input);
      wrapper.appendChild(label);
    });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleOutsideClick);
    const sizeDropdownEl = this.querySelector('[data-varaint-dropdown]');
    if (sizeDropdownEl && this._handleSizeDropdownClick) {
      sizeDropdownEl.removeEventListener('click', this._handleSizeDropdownClick);
    }
  }

  scrollToSizeDropdown(sizeDropdown) {
    // 모바일에서만 동작 (990px 이하)
    if (window.innerWidth > 990) return;

    // 열린 상태인지 확인
    if (!sizeDropdown.classList.contains('product-form__input--active')) return;

    const wrapper = sizeDropdown.querySelector('.product-form__input-wrapper');
    if (!wrapper) return;

    // opacity/visibility 전환 후 위치 계산을 위해 requestAnimationFrame 사용
    requestAnimationFrame(() => {
      const rect = wrapper.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      // 사이즈 박스 하단이 뷰포트 아래로 넘어갈 경우만 스크롤
      if (rect.bottom > viewportHeight) {
        const scrollAmount = rect.bottom - viewportHeight + 103;
        window.scrollBy({
          top: scrollAmount,
          behavior: 'smooth',
        });
      }
    });
  }

  handleOutsideClick(event) {
    const sizeDropdown = this.querySelector('[data-varaint-dropdown]');
    if (!sizeDropdown) return;

    // Check if dropdown is open
    if (!sizeDropdown.classList.contains('product-form__input--active')) return;

    // Check if click is outside the dropdown
    if (!sizeDropdown.contains(event.target)) {
      sizeDropdown.classList.remove('product-form__input--active');
      sizeDropdown.setAttribute('aria-expanded', 'false');
    }
  }

  initializeNoSelection() {
    // Only run on initial page load, not on AJAX updates
    // Check if URL has variant or option_values parameter
    const urlParams = new URLSearchParams(window.location.search);
    const hasVariantParam = urlParams.has('variant') || urlParams.has('option_values');

    if (hasVariantParam) {
      // User came with a specific variant - ensure the UI reflects this selection
      this.ensureVariantSelectionState();
      return;
    }

    // Clear all radio button selections
    const radioInputs = this.querySelectorAll('fieldset input[type="radio"]:checked');
    radioInputs.forEach(input => {
      input.checked = false;
    });

    // Clear selected value display
    const selectedValueDisplays = this.querySelectorAll('[data-selected-value]');
    selectedValueDisplays.forEach(el => {
      el.innerHTML = '';
    });

    // Mark as no variant selected, keep button enabled but hide prices
    const productInfo = this.closest('product-info');
    if (productInfo) {
      const productForm = productInfo.querySelector('product-form');
      if (productForm) {
        const submitButton = productForm.querySelector('[data-atc-btn]');
        const variantInput = productForm.querySelector('input[name="id"]');
        if (submitButton) {
          submitButton.setAttribute('data-no-variant-selected', '');
          submitButton.removeAttribute('disabled');
          // Keep prices visible even when no variant selected (shows first available variant price)
        }
        if (variantInput) {
          variantInput.value = '';
        }
      }
    }
  }

  /**
   * When page loads with variant parameter in URL, ensure the form state is correct.
   * This handles the case where server-side rendering might have set data-no-variant-selected
   * but the variant is actually selected via URL parameter.
   */
  ensureVariantSelectionState() {
    const productInfo = this.closest('product-info');
    if (!productInfo) return;

    const productForm = productInfo.querySelector('product-form');
    if (!productForm) return;

    const submitButton = productForm.querySelector('[data-atc-btn]');
    const variantInput = productForm.querySelector('input[name="id"]');

    // Check if we have a valid variant ID in the hidden input
    if (variantInput && variantInput.value) {
      // Remove the no-variant-selected flag since a variant is selected via URL
      if (submitButton && submitButton.hasAttribute('data-no-variant-selected')) {
        submitButton.removeAttribute('data-no-variant-selected');
      }
    }

    // Ensure radio buttons reflect the selected variant
    const selectedVariantData = this.querySelector('[data-selected-variant]');
    if (selectedVariantData) {
      try {
        const selectedVariant = JSON.parse(selectedVariantData.textContent);
        if (selectedVariant && selectedVariant.options) {
          // Update radio button selections based on variant options
          selectedVariant.options.forEach((optionValue, index) => {
            const fieldsets = this.querySelectorAll('fieldset');
            if (fieldsets[index]) {
              const radioInputs = fieldsets[index].querySelectorAll('input[type="radio"]');
              radioInputs.forEach(input => {
                if (input.value === optionValue) {
                  input.checked = true;
                  // Update selected value display
                  const selectedValueDisplay = fieldsets[index].querySelector('[data-selected-value]');
                  if (selectedValueDisplay) {
                    selectedValueDisplay.innerHTML = optionValue;
                  }
                }
              });
            }
          });
        }
      } catch (e) {
        console.error('Error parsing selected variant:', e);
      }
    }
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    const productId = this.dataset.productId;
    if (this.dataset.storefrontToken) {
      this.prefetchPromise = this.prefetchFromStorefrontAPI(productId, this.dataset.storefrontToken);
    }
    this.initializeRecommendations(productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 600px 0px' }
    );
    this.observer.observe(this);
  }

  getIntentFromUrl() {
    try {
      const url = new URL(this.dataset.url, window.location.origin);
      return url.searchParams.get('intent') || 'related';
    } catch {
      return 'related';
    }
  }

  getLimitFromUrl() {
    try {
      const url = new URL(this.dataset.url, window.location.origin);
      return parseInt(url.searchParams.get('limit')) || 10;
    } catch {
      return 10;
    }
  }

  async loadRecommendations(productId) {
    const storefrontToken = this.dataset.storefrontToken;

    if (storefrontToken) {
      try {
        await this.loadFromStorefrontAPI(productId, storefrontToken);
      } catch (e) {
        console.error('Storefront API failed, falling back to Ajax API:', e);
        this.loadFromAjaxAPI(productId);
      }
    } else {
      this.loadFromAjaxAPI(productId);
    }
  }

  async prefetchFromStorefrontAPI(productId, token) {
    const intent = this.getIntentFromUrl().toUpperCase();

    const query = `{
      productRecommendations(productId: "gid://shopify/Product/${productId}", intent: ${intent}) {
        id
        handle
        title
        vendor
        tags
        availableForSale
        images(first: 2) {
          edges {
            node {
              url
              altText
              width
              height
            }
          }
        }
        variants(first: 1) {
          edges {
            node {
              id
              price { amount currencyCode }
              compareAtPrice { amount currencyCode }
              availableForSale
            }
          }
        }
        metafields(identifiers: [
          {namespace: "custom", key: "emp_sl_uprc"},
          {namespace: "custom", key: "frds_sl_uprc1"},
          {namespace: "custom", key: "frds_sl_uprc2"},
          {namespace: "custom", key: "frds_sl_uprc3"},
          {namespace: "custom", key: "oms_cd"},
          {namespace: "custom", key: "gender"}
        ]) {
          key
          value
        }
      }
    }`;

    const response = await fetch('/api/2024-01/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
      },
      body: JSON.stringify({ query }),
    });

    const data = await response.json();
    return data?.data?.productRecommendations;
  }

  async loadFromStorefrontAPI(productId, token) {
    const limit = this.getLimitFromUrl();
    const products = this.prefetchPromise ? await this.prefetchPromise : await this.prefetchFromStorefrontAPI(productId, token);

    if (!products || products.length === 0) {
      if (this.style.visibility === 'hidden') {
        this.closest('section')?.remove();
      }
      return;
    }

    // "관련 상품" 섹션(related-products)에서는 '대형행사' 태그 상품도 뺀다.
    // 다른 상품으로 채우지 않으므로, 빼고 남은 추천 상품이 limit 보다 적으면 적은 그대로 보여 준다.
    const excludedTags = this.classList.contains('related-products') ? ['대형행사'] : [];
    const limitedProducts = products
      .filter(p => !p.title.includes('커스텀'))
      .filter(p => !(p.tags || []).some(tag => excludedTags.includes(tag)))
      .slice(0, limit);
    const heading = this.dataset.heading || '';
    const headingSize = this.dataset.headingSize || 'h2';
    const isSlider = this.classList.contains('custom-related-products');
    const tagsList = '공용,여성,남성,키즈,여아,남아,STANDARD 핏,SLIM핏,SEMI-OVER핏,라이프스타일,언더웨어,용품,의류,신발,드로즈'.split(',');

    const cards = limitedProducts.map((p) => this.buildCardHTML(p, tagsList)).filter(Boolean);

    if (cards.length === 0) {
      if (this.style.visibility === 'hidden') {
        this.closest('section')?.remove();
      }
      return;
    }

    let innerHTML = '';
    if (heading) {
      innerHTML += `<h2 class="related-products__heading inline-richtext ${headingSize}">${heading}</h2>`;
    }

    if (isSlider) {
      innerHTML += `<div class="rp-slider swiper" data-related-products-slider><ul class="rp-slider__track swiper-wrapper" role="list">${cards.join('')}</ul></div>`;
    } else {
      innerHTML += `<ul class="grid product-grid" role="list">${cards.map((c) => c.replace('rp-slider__slide swiper-slide', 'grid__item')).join('')}</ul>`;
    }

    this.innerHTML = innerHTML;
    this.style.removeProperty('visibility');
    this.classList.add('product-recommendations--loaded');

    this.dispatchEvent(new CustomEvent('recommendations:loaded', { bubbles: true }));
  }

  buildCardHTML(product, tagsList) {
    const images = product.images?.edges?.map((e) => e.node) || [];
    const variant = product.variants?.edges?.[0]?.node;
    if (!variant) return '';

    const shopifyId = product.id.replace('gid://shopify/Product/', '');
    const variantId = variant.id.replace('gid://shopify/ProductVariant/', '');
    const price = Math.round(parseFloat(variant.price.amount) * 100);
    const compareAt = variant.compareAtPrice ? Math.round(parseFloat(variant.compareAtPrice.amount) * 100) : 0;
    const productUrl = `/products/${product.handle}`;

    const metaMap = {};
    (product.metafields || []).forEach((m) => { if (m) metaMap[m.key] = m.value; });

    const itemId = metaMap.oms_cd || product.handle;
    const empPrice = metaMap.emp_sl_uprc ? Math.round(parseFloat(metaMap.emp_sl_uprc) * 100) : 0;
    const frds1Price = metaMap.frds_sl_uprc1 ? Math.round(parseFloat(metaMap.frds_sl_uprc1) * 100) : 0;
    const frds2Price = metaMap.frds_sl_uprc2 ? Math.round(parseFloat(metaMap.frds_sl_uprc2) * 100) : 0;
    const frds3Price = metaMap.frds_sl_uprc3 ? Math.round(parseFloat(metaMap.frds_sl_uprc3) * 100) : 0;

    const calcTier = (tierPrice, basePrice, baseCompare) => {
      if (tierPrice > 0) {
        const cmp = baseCompare > tierPrice ? baseCompare : (basePrice > tierPrice ? basePrice : 0);
        const isSale = cmp > tierPrice;
        const disc = isSale ? Math.round((cmp - tierPrice) / cmp * 100) : 0;
        return { price: tierPrice, compare: cmp, sale: isSale, discount: disc };
      }
      const isSale = baseCompare > basePrice;
      const disc = isSale ? Math.round((baseCompare - basePrice) / baseCompare * 100) : 0;
      return { price: basePrice, compare: baseCompare, sale: isSale, discount: disc };
    };

    const normal = calcTier(0, price, compareAt);
    const emp = calcTier(empPrice, price, compareAt);
    const frds1 = calcTier(frds1Price, price, compareAt);
    const frds2 = calcTier(frds2Price, price, compareAt);
    const frds3 = calcTier(frds3Price, price, compareAt);

    const donationCurrent = compareAt > price ? compareAt : price;
    const donation = { price: donationCurrent, compare: 0, sale: false, discount: 0 };

    const formatMoney = (cents) => {
      return (cents / 100).toLocaleString('ko-KR') + ' 원';
    };

    const genderTags = this.buildGenderTags(product.tags, tagsList);

    const imgSrcset = (img, widths) => {
      if (!img) return '';
      return widths
        .filter((w) => img.width >= w)
        .map((w) => `${img.url}&width=${w} ${w}w`)
        .concat([`${img.url} ${img.width}w`])
        .join(',');
    };

    const primaryImg = images[0];
    const secondaryImg = images[1];

    const primaryImgHTML = primaryImg
      ? `<img srcset="${imgSrcset(primaryImg, [165, 360, 533, 720, 940, 1066])}"
           src="${primaryImg.url}&width=533"
           sizes="(min-width: 1600px) 367px, (min-width: 990px) calc((100vw - 130px) / 4), (min-width: 750px) calc((100vw - 120px) / 3), calc((100vw - 35px) / 2)"
           alt="${this.escapeHTML(primaryImg.altText || product.title)}"
           class="motion-reduce card_image" loading="lazy"
           width="${primaryImg.width}" height="${primaryImg.height}">`
      : '';

    const secondaryImgHTML = secondaryImg
      ? `<img srcset="${imgSrcset(secondaryImg, [165, 360, 533, 720, 940, 1066])}"
           src="${secondaryImg.url}&width=533"
           sizes="(min-width: 1600px) 367px, (min-width: 990px) calc((100vw - 130px) / 4), (min-width: 750px) calc((100vw - 120px) / 3), calc((100vw - 35px) / 2)"
           alt="${this.escapeHTML(secondaryImg.altText || product.title)}"
           class="motion-reduce card_image" loading="lazy"
           width="${secondaryImg.width}" height="${secondaryImg.height}">`
      : '';

    return `<li class="rp-slider__slide swiper-slide">
      <div class="card-wrapper product-card-wrapper underline-links-hover"
        data-dl-product-card
        data-dl-item-id="${this.escapeHTML(itemId)}"
        data-dl-item-name="${this.escapeHTML(product.title)}"
        data-dl-item-price="${(price / 100).toFixed(1)}"
        data-dl-shopify-id="${shopifyId}">
        <a href="${productUrl}" class="card-wrapper__link"></a>
        <div class="card card--standard card--media" style="--ratio-percent: 100%;">
          <div class="card__inner color-scheme-2 gradient ratio" style="--ratio-percent: 100%;">
            <div class="card__media">
              <div class="media media--transparent media--hover-effect">
                ${primaryImgHTML}${secondaryImgHTML}
              </div>
            </div>
            <div class="card__content">
              <div class="card__information">
                <h3 class="card__heading">
                  <a href="${productUrl}" class="full-unstyled-link">${this.escapeHTML(product.title)}</a>
                </h3>
              </div>
            </div>
          </div>
          <div class="card__content">
            <div class="card__information">
              <span class="product_gender">${genderTags || '&nbsp;'}</span>
              <h3 class="card__heading h5" id="title--${shopifyId}">
                <a href="${productUrl}" id="CardLink--${shopifyId}" class="full-unstyled-link">${this.escapeHTML(product.title)}</a>
              </h3>
              <div class="card-information">
                <span class="caption-large light"></span>
                <div class="price-display"
                  data-variant-id="${variantId}" data-price="${normal.price}" data-available="${variant.availableForSale}"
                  ${normal.sale ? 'data-on-sale="true"' : ''}
                  data-price-normal="${normal.price}" data-compare-normal="${normal.compare}" data-discount-normal="${normal.discount}" data-sale-normal="${normal.sale}"
                  data-price-emp="${emp.price}" data-compare-emp="${emp.compare}" data-discount-emp="${emp.discount}" data-sale-emp="${emp.sale}"
                  data-price-frds1="${frds1.price}" data-compare-frds1="${frds1.compare}" data-discount-frds1="${frds1.discount}" data-sale-frds1="${frds1.sale}"
                  data-price-frds2="${frds2.price}" data-compare-frds2="${frds2.compare}" data-discount-frds2="${frds2.discount}" data-sale-frds2="${frds2.sale}"
                  data-price-frds3="${frds3.price}" data-compare-frds3="${frds3.compare}" data-discount-frds3="${frds3.discount}" data-sale-frds3="${frds3.sale}"
                  data-price-donation="${donation.price}" data-compare-donation="${donation.compare}" data-discount-donation="${donation.discount}" data-sale-donation="${donation.sale}">
                  <div class="price-display__original"${!normal.sale ? ' style="display:none"' : ''}>
                    <span class="price-display__original-price">${formatMoney(normal.compare)}</span>
                  </div>
                  <div class="price-display__current">
                    <span class="price-display__current-price">${formatMoney(normal.price)}</span>
                  </div>
                  <div class="price-display__discount"${!normal.sale ? ' style="display:none"' : ''}>
                    <span class="price-display__discount-percentage">${normal.discount}%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </li>`;
  }

  buildGenderTags(tags, tagsList) {
    if (!tags || !tagsList) return '';
    const hasGongyong = tags.includes('공용');
    const hasNama = tags.includes('남아');
    const hasYeoa = tags.includes('여아');
    const showKids = hasNama && hasYeoa;

    const collected = [];
    for (const tag of tags) {
      if (!tagsList.includes(tag)) continue;
      if (hasGongyong && (tag === '남성' || tag === '여성')) continue;
      if (showKids && (tag === '남아' || tag === '여아')) continue;
      if ((tag === '키즈') && (hasNama || hasYeoa)) continue;
      collected.push(tag);
    }
    if (showKids) collected.push('키즈');

    const unique = [...new Set(collected)];
    return unique.length > 0 ? unique.join(' | ') : '';
  }

  escapeHTML(str) {
    if (!str) return '';
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  loadFromAjaxAPI(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
          this.querySelectorAll('.card__heading a, .card__heading').forEach(el => {
            if (el.textContent.includes('커스텀')) {
              const card = el.closest('.grid__item, li, .swiper-slide');
              if (card) card.remove();
            }
          });
          this.style.removeProperty('visibility');
        } else if (this.style.visibility === 'hidden') {
          this.closest('section')?.remove();
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}
